/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */


import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {GripVertical, Edit, X} from "lucide-react";
import { cn } from "@/lib/utils";
import { FormFieldViewer } from "./form-field-viewer";
import type { FormField } from "./types";

interface SortableFormElementProps {
  element: FormField;
  onRemove: (id: string) => void;
  onSelect: (id: string) => void;
  isSelected: boolean;
  onEdit: (id: string) => void;
  isTemplateElement?: boolean;
}

export function SortableFormElement({
  element,
  onRemove,
  onSelect,
  isSelected,
  onEdit,
  isTemplateElement = false,
}: SortableFormElementProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: element.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (element.type === "extension") return null; // Skip rendering for extension type

  return (
    <div className="relative">
      {/* Drop indicator */}
      {isOver && !isDragging && (
        <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-primary rounded-full shadow-lg shadow-primary/50 z-20" />
      )}

      <div
        ref={setNodeRef}
        style={style}
        className={cn("relative", isDragging && "opacity-40")}
      >
        <Card
          className={cn(
            "border-2 transition-colors rounded-lg border-border cursor-pointer shadow-xl",
            {
              "border-primary ring-2 ring-primary/20 shadow-none": isSelected,
              "border-primary/60 bg-primary/5": isOver && !isDragging,
            }
          )}
        >
          <CardContent className="p-0">
            <div
              className="flex items-center p-2 border-b bg-muted/30"
              onClick={() =>
                element.editable && onSelect(element.id)
              }
            >
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab p-1.5 rounded hover:bg-accent shrink-0 mr-2"
                aria-label="Drag to reorder"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="flex-1 font-medium text-sm truncate">
                {element.label}
              </div>

              {element.required && (
                <Badge
                  variant="outline"
                  className="mr-2 text-xs bg-primary/10 border-primary/20"
                >
                  Required
                </Badge>
              )}

              {/* {element.editable && ( */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(element.id)}
                className="h-7 w-7 rounded-full text-primary hover:text-primary hover:bg-primary/10"
                aria-label={`Edit ${element.label}`}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
              {/* )} */}

              <div className="flex gap-1">
                {(element.deletable || isTemplateElement) && (
                  <Button
                    type="button"
                    variant="destructive"
                    size={"icon-xs"}
                    className="rounded-md"
                    onClick={() => onRemove(element.id)}
                    aria-label={`Remove ${element.label}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="p-3 pt-2">
              <FormFieldViewer field={element} value="" onChange={() => {}} disabled />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
